import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import type { Issue, TicketTriage, TriageDecide } from '@one-deployment/api'
import { Menu } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { askText } from '@/components/ask'
import { useCan } from '@/components/RoleGate'
import { enc, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'

const KIND: Record<TicketTriage['kind'], { label: string; tone: string }> = {
  bug: { label: 'Bug', tone: 'text-err' },
  error: { label: 'Error', tone: 'text-err' },
  feature: { label: 'Feature', tone: 'text-accent-hover' },
  change_request: { label: 'Change request', tone: 'text-warn' },
  feedback: { label: 'Feedback', tone: 'text-info' },
  chore: { label: 'Chore', tone: 'text-fg-subtle' },
}

/** What triage decided, in a line: type, size, area, and a Held mark when out of scope (features/07). */
export function TriageChips({ t }: { t: TicketTriage }) {
  const why = [t.scopeReason, t.scopeLines && `Scope: ${t.scopeLines}`, t.estimateHours ? `About ${t.estimateHours} h, £${Math.round(t.estimateHours * 30)}` : null, t.releaseReason && `Decided: ${t.releaseReason}${t.releasedBy ? ` (${t.releasedBy})` : ''}`].filter(Boolean).join('\n')
  return (
    <span className="flex items-center gap-1.5 text-2xs" title={why}>
      <span className={KIND[t.kind].tone}>{KIND[t.kind].label}</span>
      <span className="rounded-xs border border-border px-1 font-mono text-fg-subtle">{t.size}</span>
      {t.area && <span className="max-w-24 truncate text-fg-faint">{t.area}</span>}
      {t.held ? <span className="rounded-xs bg-warn/15 px-1 text-warn">Held · out of scope</span>
        : t.scope === 'unknown' ? <span className="text-fg-faint">scope ?</span> : null}
    </span>
  )
}

/** Re-triage for anyone who can start work; the held decision for a lead or admin. */
export function TriageMenu({ project, i }: { project: string; i: Issue }) {
  const qc = useQueryClient()
  const canStart = useCan('job.delegate', project)
  const canDecide = useCan('ticket.triage', project)
  const done = () => { void qc.invalidateQueries({ queryKey: qk.issues(project) }); void qc.invalidateQueries({ queryKey: qk.board(project) }) }
  const retriage = useMutation({ mutationFn: () => post(`/api/projects/${enc(project)}/issues/${enc(i.identifier)}/delegate`, { mode: 'classify' }), onSettled: done })
  const decide = useMutation({ mutationFn: (b: TriageDecide) => post<TicketTriage>(`/api/projects/${enc(project)}/issues/${enc(i.identifier)}/triage`, b), onSettled: done })
  const busy = i.stage === 'triage' || retriage.isPending || decide.isPending
  const items = [
    ...(canStart ? [{ label: i.triage ? 'Re-triage' : 'Triage now', onSelect: () => retriage.mutate() }] : []),
    ...(canDecide && i.triage?.held ? [
      'sep' as const,
      { label: 'In scope after all', onSelect: () => decide.mutate({ decision: 'in_scope' }) },
      { label: 'Quote as a change request', onSelect: () => decide.mutate({ decision: 'change_request' }) },
      { label: 'Decline…', danger: true, onSelect: () => void askText({ title: `Decline ${i.identifier}?`, body: 'Say why. It is posted on the ticket.', confirm: 'Decline' })
        .then((reason) => { if (reason?.trim()) decide.mutate({ decision: 'decline', reason: reason.trim() }) }) },
    ] : []),
  ]
  if (!items.length) return null
  const err = (retriage.error ?? decide.error) as Error | null
  return (
    <>
      {err && <span className="max-w-40 truncate text-2xs text-err" title={err.message}>{err.message}</span>}
      <Menu trigger={<SButton variant="ghost" size="icon-xs" aria-label="Triage actions" title="Triage actions" disabled={busy} className={cn('size-5 rounded-xs text-fg-faint hover:bg-hover hover:text-fg disabled:opacity-40')}><MoreHorizontal size={13} /></SButton>} items={items} />
    </>
  )
}
